import "./profileconnectivity.css";
import Row from "react-bootstrap/esm/Row";
import Card from "react-bootstrap/Card";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { getAsset } from "../../../../helpers/helper";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { useLocation } from "react-router-dom";
import useConnectivity from "../../../../hooks/connectivity/useConnectivity";
import dummyUserImg from "../../../../assets/images/dummy/user-avatar-img.jpg";
import { useGetApprovedMarketersQuery } from "../../../../features/marketing/marketingApiSlice";
import { encodeId } from "../../../../utils/idObfuscator";

/* ---------- BUSINESS TYPE ROUTES ---------- */
const businessTypeRoutes = {
  homemadeShop: "/Home-Made-Item-Userprofile/",
  gemShop: "/Minerl-And-Mining-UserProfile/",
  services: "/services-online/",
  usedShop: "/usedItems-userprofile/",
  rawmaterialShop: "/rawmaterial-userprofile/",
  manufacturerShop: "/manufacturer-userprofile/",
  wholesellerShop: "/wholeseller-userprofile/",
  retailerShop: "/retailer-userprofile/",
  recycledShop: "/Recycleuserprofile/",
  farmingShop: "/Farminguserprofile/",
  usedVehicleShop: "/Byvehicleuserprofile/",
  transportShop: "/buying-transport/",
  businessPropertyCompany: "/user-peronal-property/",
  foodShop: "/food-buying-profile/",
  company: "/find-jobs-online/",
  jobsCompany: "/find-jobs-online/",
  generalPublic: "/other-profile/",
  marketerProfile: "/view-marketer-profile/",
  "Approved Marketers": "/view-marketer-profile/",
};

const getProfileDisplayName = (profile) => {
  if (!profile) return "Anonymous";
  const nameVal =
    profile?.shopname ||
    (typeof profile?.shopid === "object"
      ? profile?.shopid?.shopname || profile?.shopid?.name || profile?.shopid?.title
      : null) ||
    profile?.fullname ||
    (typeof profile?.userid === "object"
      ? profile?.userid?.fullname || profile?.userid?.name
      : null) ||
    profile?.senderUser?.fullname ||
    profile?.receiverUser?.fullname ||
    profile?.sendershopname ||
    profile?.recievershopname;
  if (!nameVal || nameVal === "N/A") return "Anonymous";
  return nameVal;
};

const resolveImage = (profile) => {
  const resolveField = (field) => {
    if (!field) return null;
    if (Array.isArray(field)) {
      // Flatten nested arrays and pick first non-empty, non-placeholder value
      const first = field
        .flat(Infinity)
        .find((v) => v && v !== "N/A" && v !== "");
      return first ? getAsset(first) : null;
    }
    const str = String(field).trim();
    if (str && str !== "N/A") return getAsset(str);
    return null;
  };
  return (
    resolveField(profile?.image) ||
    resolveField(profile?.imageUrl) ||
    resolveField(profile?.profileImage) ||
    resolveField(profile?.userImage) ||
    resolveField(profile?.avatar) ||
    resolveField(profile?.sendershopimage) ||
    resolveField(profile?.recievershopimage) ||
    resolveField(
      typeof profile?.shopid === "object"
        ? profile?.shopid?.image ||
            profile?.shopid?.images ||
            profile?.shopid?.shopimage ||
            profile?.shopid?.logo ||
            profile?.shopid?.avatar
        : null,
    ) ||
    resolveField(
      typeof profile?.userid === "object"
        ? profile?.userid?.imageUrl ||
            profile?.userid?.image ||
            profile?.userid?.profileImage
        : null,
    ) ||
    resolveField(profile?.senderUser?.imageUrl) ||
    resolveField(profile?.receiverUser?.imageUrl) ||
    resolveField(profile?.senderUser?.image) ||
    resolveField(profile?.receiverUser?.image) ||
    null
  );
};

/* Shown while RTK Query is fetching so we never flash placeholder cards
 * before the privacy/hidden-connections message arrives.
 * This eliminates the "blink" effect entirely.
 */
const SkeletonCard = () => (
  <Card className="mt-2 ms-2 card_W" style={{ overflow: "hidden" }}>
    <Card.Body className="p-1">
      <div className="d-flex justify-content-between mb-2">
        <div>
          <div
            style={{
              height: 13,
              width: 80,
              background: "#e8e8e8",
              borderRadius: 4,
              marginBottom: 5,
            }}
          />
          <div
            style={{
              height: 11,
              width: 60,
              background: "#e8e8e8",
              borderRadius: 4,
            }}
          />
        </div>
      </div>
      <div className="row pt-1">
        {Array.from({ length: 6 }).map((_, i) => (
          <div className="col-4 ps-2 pe-2 text-center" key={i}>
            <div
              className="personImgSize-profile ms-auto me-auto"
              style={{
                background: "#e8e8e8",
                borderRadius: "50%",
                animationDuration: "1.4s",
              }}
            />
            <div
              style={{
                height: 10,
                width: 36,
                background: "#e8e8e8",
                borderRadius: 4,
                margin: "5px auto 0",
              }}
            />
          </div>
        ))}
      </div>
    </Card.Body>
  </Card>
);

const LoadingSkeleton = () => (
  <div className="scrollable noscroll pb-4 flex-grow-1 mt-2">
    <Row className="justify-content-center">
      {Array.from({ length: 12 }).map((_, i) => (
        <SkeletonCard key={i} />
      ))}
    </Row>
  </div>
);

/* ---------- MAIN COMPONENT ---------- */
const Profileconnectivity = ({
  shopId,
  id,
  onCardSelect,
  marketerFor: marketerForProp,
  isMarketerProfile: isMarketerProfileProp,
}) => {
  const navigate = useNavigate();
  const { name } = useSelector((state) => state.user);
  const location = useLocation();

  const isMarketerProfile =
    isMarketerProfileProp === true ||
    (isMarketerProfileProp !== false &&
      (location.pathname === "/marketing-profile" ||
        location.pathname.endsWith("/marketing-profile")));

  /* RTK Query via hook — boolean scope, never reuse the other profile's cache */
  const { cards, isLoading, message } = useConnectivity(id || name?._id, {
    isMarketerProfile,
  });

  // Use the prop passed from the parent (resolved from API via useMarketingProfileState)
  const marketerFor = marketerForProp;

  // Keep all cards as returned by the useConnectivity hook (generalPublic and marketerProfile connections are separate)
  let filteredCards = [...cards];

  // Drop empty generalPublic boxes (0 real connections) — do not show a default
  // "generalPublic" card on /user-profile when nothing is connected yet.
  filteredCards = filteredCards.filter((c) => {
    const n = String(c?.businessType || "")
      .toLowerCase()
      .replace(/[\s_-]+/g, "");
    if (n !== "generalpublic") return true;
    if (c?.isPlaceholder) return false;
    const realCount = (c?.items || []).filter(
      (it) =>
        it &&
        !it.isPlaceholder &&
        (it.userid || it.shopid || it._id || it._marketerDocId),
    ).length;
    return realCount > 0 || Number(c?.count) > 0;
  });

  const defaultTypes = [
    // generalPublic omitted from fillers — only show when real GP connections exist
    { bt: "retailerShop", title: "Retail" },
    { bt: "wholesellerShop", title: "Wholeseller" },
    { bt: "rawmaterialShop", title: "Raw Material" },
    { bt: "manufacturerShop", title: "Manufacturer" },
    { bt: "businessPropertyCompany", title: "Business Property" },
    { bt: "services", title: "Services" },
    { bt: "homemadeShop", title: "Home Made" },
    { bt: "gemShop", title: "Gem" },
    { bt: "foodShop", title: "Food" },
    { bt: "usedShop", title: "Used Items" },
    { bt: "farmingShop", title: "Farming" },
    { bt: "jobsCompany", title: "Jobs Company" },
    { bt: "marketerProfile", title: "Marketer Profile" },
  ];

  const existingBts = new Set(
    filteredCards.map((c) =>
      String(c?.businessType || "")
        .toLowerCase()
        .replace(/[\s_-]+/g, ""),
    ),
  );

  for (const def of defaultTypes) {
    if (filteredCards.length >= 12) break;
    const nDef = String(def.bt).toLowerCase().replace(/[\s_-]+/g, "");
    if (!existingBts.has(nDef)) {
      filteredCards.push({
        title: "Business Name",
        subtitle: "0 Connections",
        businessType: def.bt,
        count: 0,
        items: Array.from({ length: 6 }, () => ({ shopname: "Shop Name" })),
        isPlaceholder: true,
      });
      existingBts.add(nDef);
    }
  }

  while (filteredCards.length < 12) {
    filteredCards.push({
      title: "Business Name",
      subtitle: "0 Connections",
      businessType: "Business Name",
      count: 0,
      items: Array.from({ length: 6 }, () => ({ shopname: "Shop Name" })),
      isPlaceholder: true,
    });
  }

  // Format and prioritize a single marketerProfile card if present
  // (merge any legacy marketer / marketerShop duplicates first).
  let displayCards = [...filteredCards];
  if (!isMarketerProfile) {
    const isMarketerCard = (c) => {
      const n = String(c?.businessType || "")
        .toLowerCase()
        .replace(/[\s_-]+/g, "");
      return (
        n === "marketerprofile" ||
        n === "marketer" ||
        n === "marketershop" ||
        n === "approvedmarketers"
      );
    };

    const marketerCards = displayCards.filter(isMarketerCard);
    if (marketerCards.length > 0) {
      const mergedItems = [];
      const seen = new Set();
      marketerCards.forEach((card) => {
        (card.items || []).forEach((item) => {
          // Skip padded placeholder slots from the hook
          const userid = item?.userid?._id || item?.userid || "";
          const marketerId = item?._marketerDocId || "";
          if (!userid && !marketerId) return;
          // One tile per marketer person (ignore shopid differences)
          const key = `marketer|${userid || marketerId}`;
          if (seen.has(key)) return;
          seen.add(key);
          mergedItems.push({ ...item, shopid: null });
        });
      });
      const realItems = mergedItems;
      const paddedItems = [...mergedItems];
      while (paddedItems.length < 6) {
        paddedItems.push({ shopname: "Shop Name" });
      }
      const matchedMarketerCard = {
        ...marketerCards[0],
        title: realItems.length > 0 ? "Marketer Profile" : "Business Name",
        businessType: "marketerProfile",
        count: realItems.length,
        subtitle: `${realItems.length} Connections`,
        items: paddedItems.slice(0, 6),
        isPlaceholder: realItems.length === 0,
      };
      if (realItems.length > 0) {
        displayCards = [
          matchedMarketerCard,
          ...displayCards.filter((c) => !isMarketerCard(c)),
        ];
      } else {
        displayCards = displayCards.map((c) =>
          isMarketerCard(c)
            ? {
                ...c,
                title: "Business Name",
                subtitle: "0 Connections",
                count: 0,
                isPlaceholder: true,
              }
            : c,
        );
      }
    }
  }

  const isAllLoading = isLoading;

  /* ── 1. LOADING: skeleton only on first load (not background refetch) ── */
  if (isAllLoading) return <LoadingSkeleton />;

  /* ── 2. PRIVACY / NOT-CONNECTED message ── */
  if (
    message &&
    cards.length === 0 &&
    isMarketerProfile
  ) {
    return (
      <div className="scrollable noscroll pb-4 mt-2 flex-grow-1">
        <Card
          className="p-4 text-center border shadow-sm w-100"
          style={{ backgroundColor: "#fff" }}
        >
          <Card.Body>
            <h5 style={{ color: "#777", fontWeight: "500", margin: 0 }}>
              {message}
            </h5>
          </Card.Body>
        </Card>
      </div>
    );
  }

  /* ── 3. CONNECTION CARDS ── */
  return (
    <div className="scrollable noscroll pb-4 flex-grow-1 mt-2">
      <Row className="justify-content-center">
        {displayCards.map((data, index) => (
          <Card className="mt-2 ms-2 card_W" key={index}>
            <Card.Body className="p-1">
              {/* HEADER */}
              <div className="d-flex justify-content-between">
                <div className="font_small">
                  <h6 className="mb-0 h6_respons">{data.title}</h6>
                  <Card.Text>{data.subtitle}</Card.Text>
                </div>

                <div
                  className="font_small font-navy"
                  style={{ cursor: "pointer" }}
                >
                  <p
                    onClick={() => {
                      // Notify parent which card was selected
                      if (onCardSelect) onCardSelect(data.businessType);
                      navigate("/view-friends", {
                        state: {
                          ViewConnectedProfiles: data.businessType,
                          // Tell view-friends to use isMarketerProfile filter
                          // so the same connections shown here are shown there.
                          fromMarketerProfile: isMarketerProfile,
                        },
                      });
                    }}
                  >
                    <b>View all</b>
                  </p>
                </div>
              </div>

              {/* ITEMS */}
              <div className="row pt-3">
                {data.items.length === 0 ? (
                  <div className="col-12 text-center pb-2">
                    <p className="text-muted font_small mb-0">
                      {data.businessType === "marketerProfile"
                        ? "No new marketer found"
                        : "No connections found"}
                    </p>
                  </div>
                ) : (
                  data.items.map((profile, idx) => {
                    const viewId =
                      profile?.shopid?._id ||
                      profile?.shopid ||
                      profile?.userid?._id ||
                      profile?.userid ||
                      profile?._id;
                    const canView = Boolean(viewId);
                    // Prefer linkBusinessType when present (shop profile route).
                    const routeBT =
                      profile?.shopid && profile?.linkBusinessType
                        ? profile.linkBusinessType
                        : data.businessType;
                    const route =
                      businessTypeRoutes[routeBT] ||
                      businessTypeRoutes[data.businessType];

                    /**
                     * resolveImage handles:
                     *  • Array image fields (shop images)
                     *  • String image fields (user imageUrl from generalPublic connections)
                     *  • "N/A" sentinel → falls through to dummyUserImg
                     */
                    const resolvedSrc = resolveImage(profile);

                    return (
                      <div className="col-4 ps-2 pe-2" key={idx}>
                        <div className="text-center">
                          {/* IMAGE */}
                          <div className="personImgSize-profile ms-auto me-auto">
                            {resolvedSrc ? (
                              /* Real profile/shop image — onError falls back to dummy avatar */
                              <img
                                src={resolvedSrc}
                                alt={
                                  profile?.shopname ||
                                  profile?.fullname ||
                                  "Profile"
                                }
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                                onError={(e) => {
                                  e.currentTarget.onerror = null; // prevent loop
                                  e.currentTarget.src = dummyUserImg;
                                }}
                              />
                            ) : canView ? (
                              /* Has a real user/shop but no image → show dummy avatar */
                              <img
                                src={dummyUserImg}
                                alt="User Avatar"
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            ) : (
                              /* Pure placeholder slot (no real connection data) */
                              <AccountCircleIcon
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  color: "#0353A4",
                                }}
                              />
                            )}
                          </div>

                          {/* NAME */}
                          <div className="font_exsmall mt-1">
                            {(() => {
                              const displayName = getProfileDisplayName(profile);
                              const truncated =
                                displayName.length > 6
                                  ? displayName.slice(0, 6) + "..."
                                  : displayName;
                              return (
                                <OverlayTrigger
                                  placement="bottom"
                                  overlay={
                                    <Tooltip
                                      id={`tooltip-conn-${profile?.shopid?._id || profile?.shopid || profile?.userid?._id || profile?.userid || profile?._id || Math.random()}`}
                                    >
                                      {displayName}
                                    </Tooltip>
                                  }
                                >
                                  <p className="mb-1" style={{ cursor: "pointer" }}>
                                    {truncated}
                                  </p>
                                </OverlayTrigger>
                              );
                            })()}
                          </div>

                          {/* VIEW BUTTON */}
                          <button
                            className="follow-connt mb-2"
                            style={{
                              backgroundColor: "#0353A4",
                              color: "white",
                              border: "none",
                              padding: "1px 10px",
                              borderRadius: "15px",
                              cursor: canView ? "pointer" : "not-allowed",
                              opacity: canView ? 1 : 0.6,
                            }}
                            disabled={!canView}
                            onClick={() => {
                              if (!canView) return;
                              const userIdForProfile =
                                profile?.userid?._id ||
                                profile?.userid ||
                                (!profile?.shopid ? profile?._id : null) ||
                                null;

                              // Person / GP → /other-profile.
                              // Shop connections → that shop page (so shared
                              // posts show on the selected area Timeline tab).
                              const isShopConnection = Boolean(
                                profile?.shopid &&
                                routeBT &&
                                routeBT !== "generalPublic" &&
                                routeBT !== "marketerProfile" &&
                                routeBT !== "Approved Marketers",
                              );
                              if (
                                !isShopConnection &&
                                userIdForProfile &&
                                (routeBT === "generalPublic" ||
                                  data.businessType === "generalPublic" ||
                                  !profile?.shopid)
                              ) {
                                navigate(
                                  `/other-profile/${encodeId(String(userIdForProfile))}`,
                                );
                                return;
                              }

                              if (!route) return;
                              const finalId =
                                routeBT === "marketerProfile" ||
                                routeBT === "Approved Marketers" ||
                                data.businessType === "marketerProfile" ||
                                data.businessType === "Approved Marketers"
                                  ? encodeId(viewId)
                                  : viewId;
                              navigate(`${route}${finalId}`);
                            }}
                          >
                            View
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </Card.Body>
          </Card>
        ))}
      </Row>
    </div>
  );
};

export default Profileconnectivity;
